import { ExportFormatType, useExportAction } from "@/action/use-export-action";
import { Page, PageHeader } from "@/components/app-layout";
import { AppTopRightMenu } from "@/components/app-layout/AppTopRightMenu";
import { DeleteDialog } from "@/components/dialogs/DeleteDialog";
import { AuthorsMetadata } from "@/components/display/attributes/AuthorsMetadata";
import { ResearchIcon } from "@/components/display/display-icon/artifacts";
import { EditableTitle } from "@/components/EditableText";
import { RoleGate } from "@/components/gate/RoleGate";
// Isolated, removable feature — see _docs/GEMINI_PROGRESS_STREAMING.md
import { BrowserPageTitle } from "@/components/app-layout/BrowserPageTitle";
import { ResearchThinkingPanel } from "@/components/research/ResearchThinkingPanel";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { CollabWriter } from "@/components/Writer/CollabWriter";
import {
  TiptapContent,
  useDocumentData,
  useDocumentsData,
} from "@/data/use-documents-data";
import { useArtifactUuid } from "@/hooks/use-artifact-uuid";
import { useGroupSlug } from "@/hooks/use-group-slug";
import { useGroupUuid } from "@/hooks/use-group-uuid";
import { useKitUuid } from "@/hooks/use-kit-uuid";
import { cn } from "@/lib/utils";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEllipsisV,
  faFileCode,
  faFileWord,
  faTrash,
} from "@nnr/fontawesome-sharp-light/icons/sharp/light";
import { useNavigate } from "@tanstack/react-router";
import { useState } from "react";

export default function DocumentPage() {
  const documentUuid = useArtifactUuid("documentUuid");

  if (!documentUuid) {
    return null;
  }

  return (
    <>
      <Body documentUuid={documentUuid} />
    </>
  );
}

const Body = ({ documentUuid }: { documentUuid: string }) => {
  const groupSlug = useGroupSlug();
  const groupUuid = useGroupUuid();
  const kitUuid = useKitUuid();
  const navigate = useNavigate();

  const { document, updateDocument, deleteDocument, isDeletingDocument } =
    useDocumentData(groupUuid, kitUuid, documentUuid, { polling: true });
  const { documents } = useDocumentsData(groupUuid, kitUuid);
  const { exportDocument } = useExportAction();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  function handleUpdateName(name: string) {
    updateDocument({
      name,
    });
  }

  function handleUpdateContent(tiptapContent: TiptapContent) {
    updateDocument({
      tiptapContent,
    });
  }

  const handleDocumentDownload = (format: ExportFormatType) => {
    if (!document) return;
    exportDocument(format, document.uuid, document.name);
  };

  const handleDocumentDeletion = async () => {
    setDeleteDialogOpen(false);
    const nextDocument = documents.find((d) => d.uuid !== documentUuid);
    await deleteDocument();

    // Navigate straight to another document (or the project) so ProjectPage
    // never redirects back to the deleted document from a stale list.
    if (nextDocument) {
      navigate({
        to: "/w/$groupSlug/k/$kitUuid/document/$documentUuid",
        params: {
          groupSlug,
          kitUuid: kitUuid!,
          documentUuid: nextDocument.uuid,
        },
        replace: true,
      });
    } else {
      navigate({
        to: "/w/$groupSlug/k/$kitUuid",
        params: { groupSlug, kitUuid: kitUuid! },
        replace: true,
      });
    }
  };

  if (!document) {
    return null;
  }

  return (
    <>
      <BrowserPageTitle>{document.name}</BrowserPageTitle>
      <AppTopRightMenu.ContentPortal display="replace">
        <AuthorsMetadata
          users={document.user ? [document.user] : []}
          dateCreated={document.dateCreated}
          dateUpdated={document.dateUpdated}
        />

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="outline"
              size="icon"
              aria-label="More Document Actions"
              title="More Document Actions"
            >
              <FontAwesomeIcon icon={faEllipsisV} />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem
              className="flex flex-row items-center gap-2"
              onClick={() => handleDocumentDownload("docx")}
            >
              <FontAwesomeIcon icon={faFileWord} />
              Export Word
            </DropdownMenuItem>
            <RoleGate systemRoles={["super"]} debug>
              <DropdownMenuItem
                className="flex flex-row items-center gap-2"
                onClick={() => handleDocumentDownload("html")}
              >
                <FontAwesomeIcon icon={faFileCode} />
                Export HTML
              </DropdownMenuItem>
            </RoleGate>
            <RoleGate systemRoles={["super"]} debug>
              <DropdownMenuItem
                className="flex flex-row items-center gap-2"
                onClick={() => handleDocumentDownload("md")}
              >
                <FontAwesomeIcon icon={faFileCode} />
                Export MD
              </DropdownMenuItem>
            </RoleGate>
            <DropdownMenuItem
              className="flex flex-row items-center gap-2"
              onSelect={() => setDeleteDialogOpen(true)}
            >
              <FontAwesomeIcon icon={faTrash} />
              Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </AppTopRightMenu.ContentPortal>
      <Page document>
        <PageHeader document>
          <div className="flex flex-row items-center gap-4">
            <EditableTitle
              value={document.name}
              onUpdate={handleUpdateName}
              className="flex-1"
            />
            {document.status === "generating" && (
              <ResearchIcon className="animate-[spin_2s_linear_infinite] text-3xl" />
            )}
          </div>
        </PageHeader>
        {document.status === "generating" && (
          <PageHeader document>
            <div className={cn("text-muted-foreground flex flex-col gap-4")}>
              <div>Generating Research...</div>
              <div className="text-sm">
                Please wait, this might take a few minutes.
              </div>
              <div className="text-sm">
                This page will automatically update once the research is ready.
                Or you can leave this page and come back later.
              </div>
              {/* Isolated, removable feature — see _docs/GEMINI_PROGRESS_STREAMING.md */}
              <ResearchThinkingPanel
                groupUuid={groupUuid}
                kitUuid={kitUuid}
                documentUuid={documentUuid}
              />
            </div>
          </PageHeader>
        )}

        {document.status === "available" && document.tiptapIdentifier && (
          <CollabWriter
            key={document.uuid}
            tiptapPath={document.tiptapIdentifier}
            documentUuid={documentUuid}
            onInitialize={() => {}}
            onUpdate={handleUpdateContent}
          />
        )}
      </Page>

      <DeleteDialog
        open={deleteDialogOpen}
        disabled={isDeletingDocument}
        name={document.name}
        onOpenChange={setDeleteDialogOpen}
        onDeleteClick={handleDocumentDeletion}
      />
    </>
  );
};
